/* eslint-disable @typescript-eslint/no-unsafe-assignment */
'use client';

import {
  dealServiceCancelDeal,
  type Offer,
  offerServiceAcceptOffer,
  offerServiceDeclineOffer,
} from '@dobrozem/sdk';
import { Close } from '@radix-ui/react-dialog';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import dobrozem_sad from '@/assets/img/dobrozem-sad.svg';
import dobrozem_sucess from '@/assets/img/dobrozem-sucess.svg';
import { CancelIcon } from '@/assets/react-icons/CancelIcon';
import { Button } from '@/components/Button';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { OfferTag } from '@/components/OfferTag';
import { Responsive } from '@/components/Responsive';
import { Select } from '@/components/Select';
import { StatusModal } from '@/components/StatusModal';
import { Text } from '@/components/Text';
import { clientSdk } from '@/lib/sdk';
import { dateFormatterShort } from '@/utils/common';

import styles from './index.module.scss';

export const DealHistory = ({ deals, sold }: { deals: Offer[]; sold: boolean }) => {
  const [showAll, setShowAll] = useState(false);

  const [errorMessage, setErrorMessage] = useState<null | string>(null);

  const [currentOfferId, setCurrentOfferId] = useState<null | number>(null);

  const [currentAdvertisementId, setCurrentAdvertisementId] = useState<null | string>(null);

  const [isOpenModal, setOpenModal] = useState(false);

  const [isOpenDealCancelModal, setOpenDealCancelModal] = useState(false);

  const router = useRouter();

  const searchParams = useSearchParams();

  const [isSuccess, setSuccess] = useState(false);

  const { control, handleSubmit } = useForm();

  const declineOfferMutation = useMutation({
    mutationFn: offerServiceDeclineOffer<false>,
    onSuccess: ({ data, error }) => {
      if (data?.success) {
        setOpenModal(false);
        setCurrentOfferId(null);
        const newParams = new URLSearchParams(searchParams.toString());

        newParams.set('declineOfferMutation', new Date().getTime().toString());
        router.replace(`?${newParams.toString()}`);
      }
      if (error) {
        const message = error as { errorCode: string; message: string };

        if (message.errorCode === 'ACTIVE_DEAL_EXISTS') {
          setErrorMessage('Ви не можете відхилити угоду, тому що вже є активна угода');
        } else {
          setErrorMessage(message.message);
        }
      }
    },
  });

  const acceptOfferMutation = useMutation({
    mutationFn: offerServiceAcceptOffer<false>,
    onSuccess: ({ data, error }) => {
      if (data?.success) {
        setOpenModal(false);
        const newParams = new URLSearchParams(searchParams.toString());

        newParams.set('buyersDealMode', 'true');
        router.replace(`?${newParams.toString()}`);
      }

      if (error) {
        const message = error as { errorCode: string; message: string };

        if (message.errorCode === 'ACCEPTED_OFFER_EXISTS') {
          setErrorMessage('Ви не можете розпочати процес угоди, тому що вже є активна угода');
        } else {
          setErrorMessage(message.message);
        }
      }
    },
  });

  const cancelDealMutation = useMutation({
    mutationFn: dealServiceCancelDeal<false>,
    onSuccess: ({ data }) => {
      if (data?.success) {
        setOpenDealCancelModal(false);
        const newParams = new URLSearchParams(searchParams.toString());

        newParams.set('cancelDealMutation', new Date().getTime().toString());
        router.replace(`?${newParams.toString()}`);
      }
    },
  });

  const handleModalSucces = (id: number) => {
    setCurrentOfferId(id);
    setOpenModal(true);
    setSuccess(true);
  };

  const handleModalCancel = (id: number) => {
    setCurrentOfferId(id);
    setOpenModal(true);
    setSuccess(false);
  };

  const handleDealNextStep = () => {
    if (currentOfferId) {
      acceptOfferMutation.mutate({
        client: clientSdk,
        path: {
          id: currentOfferId,
        },
      });
    }
  };

  const handleOpenDealCancelModal = (id: string) => {
    setCurrentAdvertisementId(id);
    setOpenDealCancelModal(true);
  };

  const handleCancleDealPut = () => {
    if (currentAdvertisementId) {
      cancelDealMutation.mutate({
        body: {
          advertisementId: currentAdvertisementId,
        },
        client: clientSdk,
      });
    }
  };

  const onSubmit = handleSubmit((data) => {
    if (currentOfferId) {
      declineOfferMutation.mutate({
        body: {
          declineReason: data.declineReason,
        },
        client: clientSdk,
        path: {
          id: currentOfferId,
        },
      });
    }
  });

  const requiredRule = (isRequired: boolean) => ({
    message: 'обов’язкове поле',
    value: isRequired,
  });

  const displayedData = showAll ? deals : deals.slice(0, 4);

  const mobileStructure = (
    <div className={styles['content-list']}>
      {displayedData.map(({ advertisement, buyer, createdAt, id, proposedPrice, status }) => {
        return (
          <div className={styles['list-item']} key={id}>
            <div className={styles['inner-wrapper']}>
              <Text color='text-gray' type='p4'>
                Статус
              </Text>
              <OfferTag status={status} type='offer' />
            </div>
            <div className={styles['inner-wrapper']}>
              <Text color='text-gray' type='p4'>
                Дата
              </Text>
              <Text type='p3'>{dateFormatterShort.format(createdAt)}</Text>
            </div>
            <div className={styles['inner-wrapper']}>
              <Text color='text-gray' type='p4'>
                Від кого
              </Text>
              <Text type='p3'>
                {buyer.firstName} {buyer.lastName[0]}
              </Text>
            </div>
            <div className={styles['inner-wrapper']}>
              <Text color='text-gray' type='p4'>
                Ціна
              </Text>
              <Text type='p3'>{proposedPrice.toLocaleString('uk-UA')} грн</Text>
            </div>
            {status === 'NEW' && !sold && (
              <div className={styles['btn-wrapper']}>
                <Responsive hideFrom='tablet' style={{ width: '100%' }}>
                  <Button onClick={() => handleModalCancel(id)} red small>
                    Відхилити
                  </Button>
                </Responsive>
                <Responsive showFrom='tablet'>
                  <Button
                    icon={
                      <svg
                        fill='none'
                        height='24'
                        viewBox='0 0 24 24'
                        width='24'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          clipRule='evenodd'
                          d='M13.4154 12.0002L19.7075 5.7081C20.0984 5.31752 20.0984 4.68377 19.7075 4.29319C19.3166 3.90227 18.6835 3.90227 18.2926 4.29319L12.0005 10.5853L5.7081 4.29319C5.31718 3.90227 4.68411 3.90227 4.29319 4.29319C3.90227 4.68377 3.90227 5.31752 4.29319 5.7081L10.5856 12.0002L4.29319 18.2922C3.90227 18.6828 3.90227 19.3166 4.29319 19.7071C4.48865 19.9023 4.74481 20 5.00065 20C5.25648 20 5.51264 19.9023 5.7081 19.7068L12.0005 13.4147L18.2926 19.7068C18.488 19.9023 18.7442 20 19 20C19.2559 20 19.512 19.9023 19.7075 19.7068C20.0984 19.3162 20.0984 18.6825 19.7075 18.2919L13.4154 12.0002Z'
                          fill='white'
                          fillRule='evenodd'
                        />
                      </svg>
                    }
                    onClick={() => handleModalCancel(id)}
                    red
                    small
                  >
                    Відхилити
                  </Button>
                </Responsive>

                <Button green onClick={() => handleModalSucces(id)} small>
                  Погодитися
                </Button>
              </div>
            )}
            {status === 'ACCEPTED' && !sold && (
              <div className={styles['btn-wrapper']}>
                <Button
                  className={styles['btn-red']}
                  icon={<CancelIcon />}
                  onClick={() => handleOpenDealCancelModal(advertisement.id)}
                  red
                  small
                >
                  Скасувати угоду
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const laptopTableStructure = (
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.th}>
            <Text color='text-gray' type='p4'>
              Статус
            </Text>
          </th>
          <th className={styles.th}>
            <Text color='text-gray' type='p4'>
              Дата
            </Text>
          </th>
          <th className={styles.th}>
            <Text color='text-gray' type='p4'>
              Від кого
            </Text>
          </th>
          <th className={styles.th}>
            <Text color='text-gray' type='p4'>
              Ціна
            </Text>
          </th>
          <th className={styles.th}>{''}</th>
        </tr>
      </thead>
      <tbody>
        {displayedData.map(({ advertisement, buyer, createdAt, id, proposedPrice, status }) => {
          return (
            <tr key={id}>
              <td className={styles.td}>
                <OfferTag status={status} type='offer' />
              </td>
              <td className={styles.td}>
                <Text type='p3'>{dateFormatterShort.format(createdAt)}</Text>
              </td>
              <td className={styles.td}>
                <Text type='p3'>
                  {buyer.firstName} {buyer.lastName[0]}
                </Text>
              </td>
              <td className={styles.td}>
                <Text type='p3'>{proposedPrice.toLocaleString()} грн</Text>
              </td>
              <td className={styles.td}>
                {status === 'NEW' && !sold && (
                  <div className={styles['btn-wrapper']}>
                    <Button
                      className={styles['btn-red']}
                      icon={<CancelIcon />}
                      onClick={() => handleModalCancel(id)}
                      red
                      small
                    >
                      Відхилити
                    </Button>

                    <Button
                      className={styles['btn-green']}
                      green
                      onClick={() => handleModalSucces(id)}
                      small
                    >
                      Погодитися
                    </Button>
                  </div>
                )}
                {status === 'ACCEPTED' && !sold && (
                  <div className={styles['btn-wrapper']}>
                    <Button
                      className={styles['btn-red']}
                      icon={<CancelIcon />}
                      onClick={() => handleOpenDealCancelModal(advertisement.id)}
                      red
                      small
                    >
                      Скасувати угоду
                    </Button>
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <>
      <section className={styles.wrapper}>
        <div className={styles['content-wrapper']}>
          <div className={styles['title-wrapper']}>
            <Text type='h4'>Історія пропозицій</Text>
          </div>
          <Responsive hideFrom='tablet'>{mobileStructure}</Responsive>
          <Responsive showFrom='tablet'>{laptopTableStructure}</Responsive>
          {!showAll && deals.length > 4 && (
            <div className={styles['show-wrapper']}>
              <Button bordered onClick={() => setShowAll(true)}>
                Показати всі пропозиції
              </Button>
            </div>
          )}
        </div>
        <Modal
          className={styles.modal}
          contentClassName={styles['main-modal-content']}
          onOpenChange={setOpenModal}
          open={isOpenModal}
        >
          <div className={styles['content-modal-wrapper']}>
            {isSuccess ? (
              <>
                <Image alt='sucess' src={dobrozem_sucess} />
                <Text align='center' type='p3'>
                  Ви дійсно бажаєте розпочати процес підготовки до укладення угоди? Після прийняття
                  пропозиції від покупця, розпочнеться процес підготовки до укладення угоди і наш
                  менеджер зателефонує вам, щоб розповісти про наступні кроки
                </Text>
                <div
                  className={clsx(styles['btn-modal-wrapper'], styles['btn-modal-wrapper-success'])}
                >
                  <Close>
                    <Button asDiv bordered fullWIdth>
                      Ні
                    </Button>
                  </Close>
                  <Button
                    disabled={acceptOfferMutation.isPending}
                    fullWIdth
                    onClick={handleDealNextStep}
                  >
                    Так
                  </Button>
                </div>
                {acceptOfferMutation.isPending && (
                  <div className={styles.loader}>
                    <Loader center />
                  </div>
                )}
                {errorMessage && (
                  <Text color='red' type='p6'>
                    {errorMessage}
                  </Text>
                )}
              </>
            ) : (
              <>
                <Image alt='sucess' src={dobrozem_sad} />
                <Text align='center' color='black' tag='p' type='p3'>
                  Ви дійсно хочете відхилити цю цінову пропозицію?
                </Text>
                <form className={styles.form} onSubmit={(e) => void onSubmit(e)}>
                  <Controller
                    control={control}
                    name='declineReason'
                    render={({ field: { onChange, value }, fieldState: { error } }) => {
                      return (
                        <Select
                          error={error}
                          itemClassname={styles.declineSelectItem}
                          onValueChange={onChange}
                          options={[
                            {
                              label: 'Маю кращу пропозицію',
                              value: 'BETTER_RPICE',
                            },
                            {
                              label: 'Не влаштовує ціна',
                              value: 'PRICE',
                            },
                            {
                              label: 'Змінилися плани',
                              value: 'CHANGE_OF_PLANS',
                            },
                          ]}
                          placeholder='Причина відхилення'
                          value={value}
                        />
                      );
                    }}
                    rules={{
                      required: requiredRule(true),
                    }}
                  />

                  <div className={styles['btn-modal-wrapper']}>
                    <Close>
                      <Button asDiv bordered className={styles.modalBtn} fullWIdth>
                        Не відхиляти
                      </Button>
                    </Close>
                    <Button
                      className={styles.modalBtn}
                      disabled={declineOfferMutation.isPending}
                      fullWIdth
                      type='submit'
                    >
                      Відхилити
                    </Button>
                  </div>
                  {declineOfferMutation.isPending && (
                    <div className={styles.loader}>
                      <Loader center />
                    </div>
                  )}
                  {errorMessage && (
                    <Text color='red' type='p6'>
                      {errorMessage}
                    </Text>
                  )}
                </form>
              </>
            )}
          </div>
        </Modal>
      </section>
      <StatusModal
        buttonsChildren={
          <>
            <div className={styles.cancelDealModalBtns}>
              <Button bordered fullWIdth onClick={() => setOpenDealCancelModal(false)}>
                Не скасовувати
              </Button>
              <Button
                disabled={cancelDealMutation.isPending}
                fullWIdth
                onClick={handleCancleDealPut}
              >
                Скасувати
              </Button>
            </div>
            {cancelDealMutation.isPending && <Loader center />}
          </>
        }
        description='Ви дійсно хочете скасувати угоду?'
        logoStatus='sad'
        onOpenChange={setOpenDealCancelModal}
        open={isOpenDealCancelModal}
      />
    </>
  );
};
